/**
 * LA COULEUR ET LES INITIALES D'UNE PERSONNE — module PUR (Lot 2, 02/09/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Ce qui existait, et ce qui manquait ────────────────────────────────────
 *
 * Les INITIALES sont stockées et modifiables depuis toujours (champ de 3
 * caractères sur la fiche). Il leur manquait une seule chose : la garantie
 * qu'elles soient uniques. Elles servent d'identifiant visuel en vue Mois —
 * trente et une colonnes de pastilles où l'on ne lit que deux lettres. Deux
 * personnes qui les partagent rendent le planning du mois indéchiffrable, et
 * rien ne le signale.
 *
 * La COULEUR, elle, n'était stockée nulle part : elle était engendrée par
 * hachage du nom. Conséquence mesurée le 01/09/2026 : renommer quelqu'un
 * changeait sa couleur, et rien ne garantissait que deux personnes n'en
 * reçoivent pas deux très proches.
 *
 * ── Pourquoi la palette reprend la MÊME formule que l'algorithme ───────────
 *
 * Les couleurs engendrées valent `hsl(h 62% 92%)` de fond et `hsl(h 55% 26%)`
 * de texte — saturation et clarté fixes, seule la teinte tourne. Vérifié par le
 * calcul sur les 72 teintes du cercle : le contraste texte/fond ne descend
 * jamais sous **5,49:1**, là où l'exigence est de 4,5:1. La formule est donc
 * déjà sûre.
 *
 * La palette n'invente donc pas d'autres couleurs : elle FIXE VINGT-QUATRE TEINTES
 * dans cette même formule. Deux bénéfices qu'aucune palette écrite à part
 * n'aurait donnés : le contraste est garanti par construction, et une couleur
 * choisie est indiscernable d'une couleur engendrée — personne ne voit qui a
 * été réglé et qui ne l'a pas été.
 *
 * ⚠️ AUCUNE MIGRATION, AUCUN RECALCUL. Une personne sans couleur saisie garde
 * EXACTEMENT l'apparence qu'elle a aujourd'hui : `pastilleDe` retombe sur
 * `teinte(cle)`, à l'identique. C'est la condition posée par la spec, et elle
 * n'est pas de confort — vingt personnes qui changeraient de couleur le même
 * matin rendraient illisibles tous les plannings qu'on a en tête.
 */

/** Teinte stable pour une clé — L'ALGORITHME HISTORIQUE, inchangé.
 *  Deux exigences qui se contredisent : la même personne garde SA couleur d'un
 *  mois sur l'autre (donc une fonction du code, pas un rang dans une liste), et
 *  deux voisins de liste ne se ressemblent pas. Le pas de 137,5° — l'angle d'or
 *  — écarte au maximum les teintes successives d'une même suite. */
export function teinte(cle: string): number {
  let h = 0;
  for (let i = 0; i < cle.length; i += 1) h = (h * 31 + cle.charCodeAt(i)) >>> 0;
  return Math.round((h * 137.5) % 360);
}

export interface Pastille { background: string; color: string; borderColor: string }

/** Les trois teintes d'affichage, depuis une teinte. Saturation et clarté
 *  FIXES : c'est ce qui rend le contraste garanti quelle que soit la teinte. */
export const depuisTeinte = (h: number): Pastille => ({
  background: `hsl(${h} 62% 92%)`,
  color: `hsl(${h} 55% 26%)`,
  borderColor: `hsl(${h} 45% 78%)`,
});

/**
 * LES TEINTES PROPOSÉES — liste lue aussi par un SCRIPT SERVEUR.
 *
 * ⚠️ Forme volontairement NUE (`export const TEINTES_PALETTE = [ … ];`, pas
 * d'annotation de type, uniquement des entiers) : un script du backend la relit
 * par expression régulière, sans compiler le front. Ne pas la déguiser (pas de
 * `as const`, pas de calcul, pas de commentaire entre les crochets).
 *
 * Vingt-quatre teintes (02/10/2026, 14 d'origine conservées + 10 intercalées).
 * Toutes dans la formule `depuisTeinte` — même saturation, même clarté — donc
 * même contraste garanti. Écart minimal 12° : en dessous, deux pastilles à 92 %
 * de clarté ne se distinguent plus. Le format STOCKÉ reste `h<teinte>`.
 *
 * ⚠️ ON NE PROPOSE PAS DE SÉLECTEUR LIBRE. Un `input[type=color]` produit des
 * couleurs illisibles : sortir de cette saturation et de cette clarté fait
 * tomber le contraste sous le seuil sans que rien ne le dise à l'écran.
 */
export const TEINTES_PALETTE = [
  4, 22, 38, 52, 66, 80, 96, 113, 130, 145, 160, 172,
  184, 202, 222, 236, 250, 263, 276, 289, 302, 316, 330, 347,
];

const NOMS_TEINTES: Record<number, string> = {
  4: 'Rouge', 22: 'Brique', 38: 'Orange', 52: 'Ambre', 66: 'Jaune', 80: 'Olive',
  96: 'Vert pomme', 113: 'Vert prairie', 130: 'Vert', 145: 'Vert menthe',
  160: 'Émeraude', 172: 'Vert d’eau', 184: 'Turquoise', 202: 'Cyan', 222: 'Bleu',
  236: 'Bleu roi', 250: 'Indigo', 263: 'Pervenche', 276: 'Violet', 289: 'Pourpre',
  302: 'Mauve', 316: 'Fuchsia', 330: 'Rose', 347: 'Framboise',
};

/** La palette avec ses noms, pour l'affichage. */
export const PALETTE: { teinte: number; nom: string }[] = TEINTES_PALETTE
  .map((t) => ({ teinte: t, nom: NOMS_TEINTES[t] || `Teinte ${t}` }));

/** La valeur STOCKÉE pour une teinte de la palette. On garde la teinte et non
 *  un hexadécimal : c'est elle qui permet de recomposer les trois nuances par
 *  la même formule, donc de conserver la garantie de contraste. */
export const valeurCouleur = (h: number) => `h${h}`;

/** Lecture d'une valeur stockée. `null` = rien de valide, on retombera sur
 *  l'algorithme — jamais sur une couleur par défaut, qui serait un choix qu'on
 *  ferait à la place de quelqu'un. */
export function teinteStockee(couleur?: string | null): number | null {
  const v = String(couleur || '').trim();
  const m = /^h(\d{1,3})$/.exec(v);
  if (!m) return null;
  const h = Number(m[1]);
  return h >= 0 && h <= 360 ? h : null;
}

/**
 * LA pastille d'une personne — le seul point d'entrée.
 *
 * ⚠️ Tous les écrans passent par ici : le mois, la semaine, l'organigramme.
 * Une seconde implémentation ferait diverger l'apparence d'un écran à l'autre,
 * et c'est précisément ce que la spec interdit (« une personne ne doit jamais
 * changer d'apparence d'un écran à l'autre »).
 */
export function pastilleDe(cle: string, couleur?: string | null): Pastille {
  return depuisTeinte(teinteEffective(cle, couleur));
}

/** La teinte RÉELLEMENT affichée : celle choisie, sinon le hachage de la clé.
 *  C'est la valeur de départ du sélecteur de la fiche — la fiche, l'organigramme
 *  et le planning montrent ainsi la même couleur, reliée au même champ. */
export function teinteEffective(cle: string, couleur?: string | null): number {
  return teinteStockee(couleur) ?? teinte(cle);
}

export interface PersonneCouleur {
  orgId: string; nom: string;
  /** Les cases de l'organigramme fonctionnel tenues (`rh-personnel.fonctionIds`). */
  fonctionIds?: unknown[] | null;
  statut?: string | null; dateSortie?: string | null;
  couleur?: string | null; externe?: boolean;
}

/** 360 ≡ 0 sur le cercle : « h360 » et « h0 » sont la même couleur. */
const surLeCercle = (t: number) => ((t % 360) + 360) % 360;

/**
 * LES TEINTES DÉJÀ PRISES par les collègues de MÊME FONCTION (RQ, 02/10/2026) :
 * teinte → nom de la personne. Deux préparateurs de même couleur rendent le
 * planning illisible ; deux personnes de fonctions différentes, non.
 *
 * ⚠️ MIROIR DE `backend/src/couleurs-fonction.ts` — c'est LUI qui refuse (409),
 * ceci ne fait que le dire plus tôt. Mêmes règles :
 *  · « même fonction » = les `fonctionIds` se RECOUPENT (une personne à deux
 *    fonctions est comparée dans chacune) ; sans fonction, pas de collègue ;
 *  · seules comptent les personnes EN FONCTION : ni externes, ni sorties
 *    (statut `sorti`, ou `dateSortie` atteinte) — un statut `suspendu` NE
 *    libère PAS la couleur : la personne revient, on ne la lui reprend pas ;
 *  · on compare les teintes EFFECTIVES : un collègue sans couleur choisie en
 *    porte une quand même (son hachage), et c'est elle qu'on voit au planning.
 */
export function couleursPrises(
  personnes: PersonneCouleur[], orgId: string, fonctionIds: unknown[], aujourdhui: string,
): Map<number, string> {
  const miennes = new Set((fonctionIds || []).map(String).filter(Boolean));
  const prises = new Map<number, string>();
  if (!miennes.size) return prises;
  for (const p of personnes) {
    if (p.orgId === orgId || p.externe || p.statut === 'sorti') continue;
    const sortie = String(p.dateSortie || '').slice(0, 10);
    if (sortie && sortie <= aujourdhui) continue;
    if (!(p.fonctionIds || []).some((x) => miennes.has(String(x)))) continue;
    const t = surLeCercle(teinteEffective(p.orgId, p.couleur));
    if (!prises.has(t)) prises.set(t, p.nom);
  }
  return prises;
}

/* ═══════════════════════ LA COULEUR D'UN POSTE ═══════════════════════
 *
 * RQ, 05/10/2026 : « Dans Mon planning, affiche les postes avec la couleur de
 * chaque poste comme le planning mensuel. Et je voudrais que le choix de la
 * couleur du poste soit aussi paramétrable dans les postes (comme les couleurs
 * de personnes) : une couleur déjà prise ne peut pas se doubler. »
 *
 * ── Ce qui existait, et ce qui manquait ───────────────────────────────────
 *
 * Le planning mensuel colorait déjà ses pastilles de poste — mais par HACHAGE
 * du code, sans que personne puisse choisir. Deux conséquences, mesurées sur
 * les 24 postes actifs : 22 teintes seulement (`P2_CHIMIE` et `P3_HORMONO`
 * tombent tous deux sur 195, `BIOLOGISTE` et `PLVPM` sur 203 — deux paires
 * indiscernables que rien ne signalait), et une couleur qui changeait si l'on
 * renommait le poste.
 *
 * ⚠️ AUCUNE MIGRATION : un poste sans couleur choisie garde EXACTEMENT
 * l'apparence qu'il a aujourd'hui (`pastilleDe(code, null)` retombe sur
 * `teinte(code)`). C'est la même condition que pour les personnes, et elle
 * n'est pas de confort — vingt-quatre postes qui changeraient de couleur le
 * même matin rendraient illisibles tous les plannings qu'on a en tête.
 *
 * ── L'unicité est PAR CATÉGORIE (arbitrage de la RQ, 05/10/2026) ─────────
 *
 * « Restreindre les couleurs par catégorie. Cela est amplement suffisant. »
 *
 * Et c'est le bon découpage, pas seulement le plus simple : la catégorie est
 * EXACTEMENT le bloc que le planning dessine (`blocDeCategorie`, un bloc par
 * catégorie, un pour un). Les postes d'une même catégorie sont donc ceux qui
 * s'affichent côte à côte — ce sont ceux-là qu'il faut pouvoir distinguer. Deux
 * postes de catégories différentes ne se voisinent dans aucun bloc.
 *
 * Une unicité GLOBALE était satisfaisable, mais tout juste : 24 teintes pour 24
 * postes actifs, donc une palette saturée où le 25e poste n'aurait plus trouvé
 * de teinte libre. Par catégorie, le plus gros bloc compte 7 postes.
 *
 * ⚠️ CE QUE CELA CONCÈDE : dans « Mon planning », une personne voit dans le même
 * mois des postes de plusieurs catégories — deux d'entre eux PEUVENT donc
 * porter la même teinte. L'écran ne promet pas le contraire : sa légende dit
 * que la couleur est celle du poste, pas qu'elle est unique.
 *
 * ⚠️ POURQUOI ICI, et non dans un module « couleurs-poste » à part : la règle a
 * besoin de `teinteEffective`, donc du hachage. Un second fichier aurait dû
 * l'importer — et un module testable hors navigateur ne doit rien importer
 * à l'exécution (garde-fou de `npm test`) — ou le recopier, et un second
 * hachage finirait par diverger de celui du planning.
 *
 * ⚠️ MIROIR DE `backend/src/couleurs-poste.ts` — c'est LUI qui refuse (409) ;
 * ceci ne fait que le dire plus tôt, et griser la teinte dans la liste.
 */

export interface PosteCouleur {
  code: string;
  libelle?: string | null;
  /** C'est elle qui décide du groupe de comparaison — voir `blocDuPoste`. */
  categorie?: string | null;
  couleur?: string | null;
  actif?: boolean | null;
}

/** Le groupe de comparaison d'un poste = son bloc de planning.
 *  ⚠️ MÊME RÈGLE que `blocDeCategorie` (`@/lib/blocs-planning`), recopiée ici
 *  et non importée : ce module doit rester une FEUILLE sans import d'exécution
 *  (il est compilé et éprouvé hors navigateur par `npm test`). Les deux listes
 *  de catégories sont comparées par `backend/tests/couleurs-poste.test.js` —
 *  elles ne peuvent pas diverger en silence. */
const CATEGORIES_CONNUES = [
  'reception', 'technique', 'garde', 'validation', 'qualite', 'services', 'dimanche',
];
const blocDuPoste = (categorie?: string | null) => {
  const c = String(categorie || '').trim();
  return CATEGORIES_CONNUES.includes(c) ? c : 'autres';
};

/**
 * LES TEINTES DÉJÀ PRISES par les autres postes de LA MÊME CATÉGORIE :
 * teinte → libellé du poste.
 *
 * Mêmes règles que le serveur :
 *  · même catégorie seulement — c'est le bloc du planning (voir l'en-tête) ;
 *    une catégorie vide ou inconnue rejoint « Autres postes », et les postes
 *    qui y tombent se comparent entre eux : ils s'y affichent côte à côte ;
 *  · seuls les postes ACTIFS comptent — un poste désactivé ne figure plus au
 *    planning, sa couleur est libre. ⚠️ La fenêtre de validité
 *    (`valableDu`/`valableAu`) ne libère RIEN : un poste propre au Ramadan
 *    reste un poste qu'on reverra, et lui reprendre sa couleur le ferait
 *    changer d'apparence d'une année sur l'autre ;
 *  · on compare les teintes EFFECTIVES — un poste sans couleur choisie en
 *    porte une quand même (son hachage), et c'est elle qu'on voit au planning.
 */
export function couleursPrisesPostes(
  postes: PosteCouleur[], code: string, categorie?: string | null,
): Map<number, string> {
  const bloc = blocDuPoste(categorie);
  const prises = new Map<number, string>();
  for (const p of postes) {
    const c = String(p.code || '');
    if (!c || c === code || p.actif === false) continue;
    if (blocDuPoste(p.categorie) !== bloc) continue;
    const t = surLeCercle(teinteEffective(c, p.couleur));
    if (!prises.has(t)) prises.set(t, String(p.libelle || c));
  }
  return prises;
}

/** La teinte RÉELLEMENT affichée pour un poste : celle choisie, sinon le
 *  hachage de son code. C'est la valeur de départ du sélecteur de la fiche —
 *  la fiche et le planning montrent ainsi la même couleur. */
export const teinteDuPoste = (code: string, couleur?: string | null): number =>
  teinteEffective(code, couleur);

/* ─────────────────────────── Initiales ─────────────────────────── */

/** Le repli HISTORIQUE : la première lettre des deux premiers mots. */
export const initialesBrutes = (nom: string) => nom.trim().split(/\s+/).filter(Boolean)
  .slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '??';

/**
 * Le repli, DÉBARRASSÉ DE SES COLLISIONS (bug latent confirmé le 02/09/2026).
 *
 * `initialesBrutes` ne résout rien : sur l'effectif réel, « Mehdi Ben Salem » et
 * « Mezri Belhadj Amor » rendent tous deux `MB`, « Sayed Med Tayeb Abidi » et
 * « Souha Mokni » tous deux `SM`. Aucune collision ne se voit aujourd'hui parce
 * que tout le monde a des initiales SAISIES — le défaut attendait la première
 * arrivée sans saisie.
 *
 * ⚠️ L'ORDRE D'ENTRÉE DÉCIDE, et il est stable : `deja` est rempli dans l'ordre
 * de la liste, et le premier arrivé garde la forme courte. Une résolution qui
 * dépendrait d'un tri instable ferait permuter deux pastilles d'un affichage à
 * l'autre — pire qu'une collision, parce qu'on ne la verrait pas.
 */
export function initialesSansCollision(nom: string, deja: Set<string>): string {
  const base = initialesBrutes(nom);
  if (!deja.has(base)) return base;
  const mots = nom.trim().split(/\s+/).filter(Boolean);
  /* On allonge avec la troisième lettre du NOM DE FAMILLE, puis du prénom :
     `MB` → `MBE` (Ben) → `MEB` … Toujours trois caractères au plus, c'est la
     contrainte du champ et celle de la pastille. */
  const candidats: string[] = [];
  if (mots[1]) candidats.push((base + mots[1][1]).slice(0, 3).toUpperCase());
  if (mots[0]) candidats.push((mots[0][0] + mots[0][1] + (mots[1]?.[0] || '')).slice(0, 3).toUpperCase());
  if (mots[2]) candidats.push((mots[0][0] + mots[1][0] + mots[2][0]).slice(0, 3).toUpperCase());
  for (const c of candidats) if (c.length >= 2 && !deja.has(c)) return c;
  /* Dernier recours : un chiffre. Laid, et c'est voulu — il se voit, donc il
     appelle une saisie à la main. Rendre une collision jolie la rend durable. */
  for (let n = 2; n <= 9; n += 1) {
    const c = `${base.slice(0, 2)}${n}`;
    if (!deja.has(c)) return c;
  }
  return base;
}
